import type { float, Nullable } from "core/types";
import type { RewardGraphConnectionPoint } from "./rewardGraphConnectionPoint";
import { RewardGraphContextualSources } from "./rewardGraphContextualSources";
import { Quaternion, Vector3 } from "core/Maths/math.vector";
import type { IRewardGraphExecutionContext } from "./interfaces/rewardGraphExecutionContext";
import { RewardGraphConnectionPointTypes } from "./rewardGraphConnectionPointTypes";
import { IRewardGraphInstancingContext } from "./interfaces/rewardGraphInstancingContext";
import { IRewardGraphBuildQueryContext } from "./interfaces/rewardGraphBuildQueryContext";

/**
 * Class used to store node based reward build state
 */
export class RewardGraphBuildState {
    /** Gets or sets the list of non connected mandatory inputs */
    public notConnectedNonOptionalInputs: RewardGraphConnectionPoint[] = [];
    /** Gets or sets the list of non contextual inputs having no contextudal data */
    public noContextualData: RewardGraphContextualSources[] = [];
    /** Gets or sets the build identifier */
    public buildId: number;
    /** Gets or sets a boolean indicating that verbose mode is on */
    public verbose: boolean;

    public rewardData: float;
    public stopData: float;
    public waypointData: Vector3;

    private _executionContext: Nullable<IRewardGraphExecutionContext> = null;
    private _instancingContext: Nullable<IRewardGraphInstancingContext> = null;

    private _executionContextStack: Array<Nullable<IRewardGraphExecutionContext>> = [];
    private _instancingContextStack: Array<Nullable<IRewardGraphInstancingContext>> = [];

    public queryContext? : IRewardGraphBuildQueryContext;

    /** Gets or sets the execution context */
    public get executionContext() {
        return this._executionContext;
    }

    /** Gets or sets the instancing context */
    public get instancingContext() {
        return this._instancingContext;
    }

    /**
     * Push the new active execution context
     * @param executionContext defines the execution context
     */
    public pushExecutionContext(executionContext: IRewardGraphExecutionContext) {
        this._executionContext = executionContext;
        this._executionContextStack.push(this._executionContext);
    }

    /**
     * Push the new active instancing context
     * @param instancingContext defines the instancing context
     */
    public pushInstancingContext(instancingContext: IRewardGraphInstancingContext) {
        this._instancingContext = instancingContext;
        this._instancingContextStack.push(this._instancingContext);
    }

    /**
     * Remove current execution context and restore the previous one
     */
    public restoreExecutionContext() {
        this._executionContextStack.pop();
        this._executionContext = this._executionContextStack.length > 0 ? this._executionContextStack[this._executionContextStack.length - 1] : null;
    }

    captureState(stateName: string, arg1: any) {
        throw new Error("Capture State Method not implemented.");
    }

    retrieveState(stateName: string): any {
        throw new Error("Retrive State Method not implemented.");
    }

    /**
     * Remove current isntancing context and restore the previous one
     */
    public restoreInstancingContext() {
        this._instancingContextStack.pop();
        this._instancingContext = this._instancingContextStack.length > 0 ? this._instancingContextStack[this._instancingContextStack.length - 1] : null;
    }

    /**
     * Gets the value associated with a contextual source
     * @param source Source of the contextual value
     * @param skipWarning Do not store the warning for reporting if true
     * @returns the value associated with the source
     */
    public getContextualValue(source: RewardGraphContextualSources, skipWarning = false) {
        if (!this.executionContext) {
            if (!skipWarning) {
                this.noContextualData.push(source);
            }
            return null;
        }

        //const index = this.executionContext.getExecutionIndex();

        switch (source) {
            case RewardGraphContextualSources.None:
                return Vector3.Zero();
        }

        return null;
    }

    /**
     * Adapt a value to a target type
     * @param source defines the value to adapt
     * @param targetType defines the target type
     * @returns the adapted value
     */
    adapt(source: RewardGraphConnectionPoint, targetType: RewardGraphConnectionPointTypes) {
        const value = source.getConnectedValue(this) || 0;

        if (source.type === targetType) {
            return value;
        }

        switch (targetType) {
            case RewardGraphConnectionPointTypes.Vector3:
                return new Vector3(value, value, value);
            case RewardGraphConnectionPointTypes.Quat:
                return new Quaternion(value, value, value, value);
        }

        return null;
    }

    /**
     * Adapt an input value to a target type
     * @param source defines the value to adapt
     * @param targetType defines the target type
     * @param defaultValue defines the default value to use if not connected
     * @returns the adapted value
     */
    adaptInput(source: RewardGraphConnectionPoint, targetType: RewardGraphConnectionPointTypes, defaultValue: any) {
        if (!source.isConnected) {
            return source.value || defaultValue;
        }

        const value = source.getConnectedValue(this);

        if (source._connectedPoint?.type === targetType) {
            return value;
        }

        switch (targetType) {
            case RewardGraphConnectionPointTypes.Vector3:
                return new Vector3(value, value, value);
            case RewardGraphConnectionPointTypes.Quat:
                return new Quaternion(value, value, value, value);
        }

        return null;
    }

    /**
     * Emits console errors and exceptions if there is a failing check
     */
    public emitErrors() {
        let errorMessage = "";

        for (const notConnectedInput of this.notConnectedNonOptionalInputs) {
            errorMessage += `input ${notConnectedInput.name} from block ${
                notConnectedInput.ownerBlock.name
            }[${notConnectedInput.ownerBlock.getClassName()}] is not connected and is not optional.\n`;
        }

        for (const source of this.noContextualData) {
            errorMessage += `Contextual input ${RewardGraphContextualSources[source]} has no context to pull data from (must be connected to a setXXX block or a instantiateXXX block).\n`;
        }

        if (errorMessage) {
            // eslint-disable-next-line no-throw-literal
            throw "Build of Reward Graph failed:\n" + errorMessage;
        }
    }
}
